import React, { useEffect, useState, useContext } from "react";
import AppContext from "../../contexts/AppContext";
import { Line } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import dayjs from "dayjs";
import moment from "moment";
import { hasElevatedSessionAccess } from "../../utils/sessionPermissions";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, ArcElement, ChartDataLabels);

const CHART_COLORS = [
    "rgba(59,130,246,1)",
    "rgba(16,185,129,1)",
    "rgba(245,158,11,1)",
    "rgba(239,68,68,1)",
    "rgba(139,92,246,1)",
    "rgba(20,184,166,1)",
    "rgba(236,72,153,1)",
    "rgba(99,102,241,1)",
];

const formatDate = (date) => date.toISOString().split("T")[0];

const generateDateRange = (start, end) => {
    const current = new Date(start);
    const last = new Date(end);
    const dates = [];

    while (current <= last) {
        dates.push(formatDate(current));
        current.setDate(current.getDate() + 1);
    }

    return dates;
};

const normalizeSpotDetails = (entry) => (Array.isArray(entry?.spotDetails) ? entry.spotDetails : []);
const normalizeRecordCount = (value) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : 0;
};

const ErrorNotice = ({ message }) => (
    <div className="mt-4 rounded-[18px] border border-[#f4c7c3] bg-[#fff4f2] px-4 py-4 text-sm text-[#b5473c]">
        {message}
    </div>
);

const AdvLogDashboard = () => {
    const { selectedRole, token, userInfo } = useContext(AppContext);
    const [advLogData, setAdvLogData] = useState([]);
    const [selectedSpotId, setSelectedSpotId] = useState("all");
    const [loading, setLoading] = useState(false);

    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const sevenDaysAgo = new Date(yesterday);
    sevenDaysAgo.setDate(yesterday.getDate() - 6);

    const [startDate, setStartDate] = useState(sevenDaysAgo);
    const [endDate, setEndDate] = useState(yesterday);
    const [timeRange, setTimeRange] = useState("7");
    const [maxTicks, setMaxTicks] = useState(window.innerWidth < 640 ? 4 : 15);
    const [error, setError] = useState(null);

    const roleArray = userInfo?.roleInfo?.[selectedRole?.type] || [];
    const currentRole = roleArray.find((r) => r.id === selectedRole?.id);
    const permissionLevel = currentRole?.permissionLevel;
    const canShowAdvanced = hasElevatedSessionAccess(userInfo, permissionLevel);

    useEffect(() => {
        const handleResize = () => {
            setMaxTicks(window.innerWidth < 640 ? 4 : 20);
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        if (!canShowAdvanced && timeRange === "90") {
            const currentYesterday = new Date();
            currentYesterday.setDate(currentYesterday.getDate() - 1);
            const newEndDate = new Date(currentYesterday);
            const newStartDate = new Date(newEndDate);
            newStartDate.setMonth(newEndDate.getMonth() - 1);
            newStartDate.setDate(newStartDate.getDate() + 1);
            setTimeRange("30");
            setStartDate(newStartDate);
            setEndDate(newEndDate);
        }
    }, [canShowAdvanced, timeRange]);

    const handleTimeRangeChange = (e) => {
        const range = ["7", "14", "30", "90"].includes(e.target.value) ? e.target.value : "7";
        setTimeRange(range);

        const newEndDate = new Date(yesterday);
        const newStartDate = new Date(newEndDate);

        if (range === "7") {
            newStartDate.setDate(newEndDate.getDate() - 6);
        } else if (range === "14") {
            newStartDate.setDate(newEndDate.getDate() - 13);
        } else if (range === "30") {
            newStartDate.setMonth(newEndDate.getMonth() - 1);
            newStartDate.setDate(newStartDate.getDate() + 1);
        } else if (range === "90") {
            newStartDate.setMonth(newEndDate.getMonth() - 3);
            newStartDate.setDate(newStartDate.getDate() + 1);
        }

        setStartDate(newStartDate);
        setEndDate(newEndDate);
    };

    useEffect(() => {
        let ignore = false;

        const fetchAdvLogData = async () => {
            if (!selectedRole?.id || selectedRole?.type !== "adv") {
                if (!ignore) {
                    setAdvLogData([]);
                    setError(null);
                }
                return;
            }

            try {
                if (!ignore) {
                    setLoading(true);
                    setError(null);
                }
                const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                const localStart = new Date(startDate);
                const localEnd = new Date(endDate);
                localStart.setHours(0, 0, 0, 0);
                localEnd.setHours(23, 59, 59, 999);

                const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/private/analysis/advertisement/log/summary/read`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        adv_id: selectedRole.id,
                        startDate: dayjs(localStart).format("YYYY-MM-DDTHH:mm:ss.SSS"),
                        endDate: dayjs(localEnd).format("YYYY-MM-DDTHH:mm:ss.SSS"),
                        userTimeZone,
                    }),
                });

                if (!response.ok) {
                    const err = await response.json().catch(() => null);
                    throw new Error(err?.error || response.statusText || "Failed to load advertisement analytics.");
                }

                const data = await response.json();
                if (!ignore) {
                    setAdvLogData(Array.isArray(data) ? data : []);
                }
            } catch (error) {
                console.error("Failed to fetch data:", error);
                if (!ignore) {
                    setAdvLogData([]);
                    setError(error?.message || "Failed to load advertisement analytics.");
                }
            } finally {
                if (!ignore) {
                    setLoading(false);
                }
            }
        };

        fetchAdvLogData();
        return () => {
            ignore = true;
        };
    }, [endDate, selectedRole, startDate, token]);

    useEffect(() => {
        setSelectedSpotId("all");
    }, [selectedRole?.id]);

    const handleSpotChange = (e) => {
        setSelectedSpotId(e.target.value);
    };

    const calcStepSize = (values) => {
        if (!Array.isArray(values) || !values.length) return undefined;

        const maxVal = Math.max(...values);

        if (maxVal <= 10) return 1;
        if (maxVal <= 50) return 5;
        if (maxVal <= 100) return 10;
        if (maxVal <= 500) return 50;
        if (maxVal <= 1000) return 100;
        if (maxVal <= 5000) return 500;
        return undefined;
    };

    const uniqueSpots = Array.from(
        advLogData
            .flatMap((entry) =>
                normalizeSpotDetails(entry).map((spot) => ({
                    spotId: spot.venue_id,
                    spotName: spot.venueName,
                }))
            )
            .reduce((map, spot) => map.set(spot.spotId, spot.spotName), new Map())
    );

    const calculateTotals = () => {
        const dateRange = generateDateRange(startDate, endDate);

        return dateRange.map((date) => {
            const entry = advLogData.find((item) => item.date === date);
            return {
                date,
                totalRecords: entry
                    ? normalizeSpotDetails(entry).reduce((sum, spot) => sum + normalizeRecordCount(spot.totalRecords), 0)
                    : 0,
            };
        });
    };

    const calculateSpotTotals = () => {
        const dateRange = generateDateRange(startDate, endDate);

        if (selectedSpotId === "all") {
            const hotelData = {};

            advLogData.forEach((entry) => {
                normalizeSpotDetails(entry).forEach((spot) => {
                    if (!hotelData[spot.venue_id]) {
                        hotelData[spot.venue_id] = {
                            name: spot.venueName,
                            data: [],
                        };
                    }

                    hotelData[spot.venue_id].data.push({
                        date: entry.date,
                        totalRecords: normalizeRecordCount(spot.totalRecords),
                    });
                });
            });

            return Object.entries(hotelData).map(([spotId, details]) => ({
                spotId,
                spotName: details.name,
                data: dateRange.map((date) => {
                    const record = details.data.find((item) => item.date === date);
                    return {
                        date,
                        totalRecords: record ? record.totalRecords : 0,
                    };
                }),
            }));
        }

        return dateRange.map((date) => {
            const entry = advLogData.find((item) => item.date === date);
            const spotDetails = normalizeSpotDetails(entry).find((spot) => spot.venue_id === selectedSpotId);
            return {
                date,
                totalRecords: spotDetails ? normalizeRecordCount(spotDetails.totalRecords) : 0,
            };
        });
    };

    const totals = calculateTotals();
    const totalRecordsValues = totals.map((item) => item.totalRecords);
    const spotTotals = calculateSpotTotals();
    const selectedSpotName = uniqueSpots.find(([id]) => id === selectedSpotId)?.[1] || "All Spots";

    const totalRecordsLineData = {
        labels: totals.map((item) => item.date),
        datasets: [
            {
                label: "Total Records",
                data: totalRecordsValues,
                borderColor: "rgba(75,192,192,1)",
                backgroundColor: "rgba(75,192,192,0.2)",
            },
        ],
    };

    const filteredSpotTotalsData = {
        labels: generateDateRange(startDate, endDate),
        datasets:
            selectedSpotId === "all"
                ? spotTotals.map((spot, index) => ({
                    label: spot.spotName,
                    data: spot.data.map((entry) => entry.totalRecords),
                    borderColor: CHART_COLORS[index % CHART_COLORS.length],
                    backgroundColor: "rgba(0,0,0,0)",
                }))
                : [
                    {
                        label: `Total Records (${selectedSpotName})`,
                        data: spotTotals.map((item) => item.totalRecords),
                        borderColor: "rgba(255,99,132,1)",
                        backgroundColor: "rgba(255,99,132,0.2)",
                    },
                ],
    };

    const filteredSpotValues =
        selectedSpotId === "all"
            ? filteredSpotTotalsData.datasets.flatMap((dataset) => dataset.data)
            : filteredSpotTotalsData.datasets[0]?.data || [];

    const optionsForLineChart = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            datalabels: {
                display: false,
            },
            legend: {
                display: false,
            },
        },
        scales: {
            x: {
                title: {
                    display: true,
                    text: "Time (Date)",
                    font: {
                        size: 14,
                        weight: "bold",
                    },
                    color: "#4B5563",
                },
                ticks: {
                    maxTicksLimit: maxTicks,
                    callback(value) {
                        const rawDateStr = this.getLabelForValue(value);
                        const dateObj = moment(rawDateStr, "YYYY-MM-DD");
                        return dateObj.format("MMM D");
                    },
                },
            },
            y: {
                beginAtZero: true,
                ticks: {
                    stepSize: calcStepSize(totalRecordsValues),
                },
                title: {
                    display: true,
                    text: "Click counts",
                    font: {
                        size: 14,
                        weight: "bold",
                    },
                    color: "#4B5563",
                },
            },
        },
    };

    const optionsForMutiLine = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            datalabels: {
                display: false,
            },
            legend: {
                onClick: () => {},
            },
        },
        scales: {
            x: {
                title: {
                    display: true,
                    text: "Time (Date)",
                    font: {
                        size: 14,
                        weight: "bold",
                    },
                    color: "#4B5563",
                },
                ticks: {
                    maxTicksLimit: maxTicks,
                    callback(value) {
                        const rawDateStr = this.getLabelForValue(value);
                        const dateObj = moment(rawDateStr, "YYYY-MM-DD");
                        return dateObj.format("MMM D");
                    },
                },
            },
            y: {
                beginAtZero: true,
                ticks: {
                    stepSize: calcStepSize(filteredSpotValues),
                },
                title: {
                    display: true,
                    text: "Click counts",
                    font: {
                        size: 14,
                        weight: "bold",
                    },
                    color: "#4B5563",
                },
            },
        },
    };

    return (
        <div className=" p-6 bg-gray-50 rounded-lg shadow-lg">
            <div className="mb-4 flex justify-between items-center">
                <div className="flex gap-4 items-center">
                    <label className="text-gray-600 font-medium">
                        Time Range:
                        <select
                            value={timeRange}
                            onChange={handleTimeRangeChange}
                            className="ml-2 p-2 border rounded"
                        >
                            <option value="7">Past 7 Days</option>
                            <option value="14">Past 14 Days</option>
                            <option value="30">Past 30 Days</option>
                            {canShowAdvanced && <option value="90">Past 3 Months</option>}
                        </select>
                    </label>
                </div>
            </div>

            <div className="h-[300px] mb-4">
                <h3 className="text-center font-semibold">
                    {" "}Total Advertisement Clicks
                    <span className="ml-2 relative group cursor-pointer">
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-300 text-gray-700 text-sm font-bold">
                            ?
                        </span>
                        <div className="absolute hidden group-hover:block bg-white text-gray-700 text-sm p-2 rounded shadow-md whitespace-normal w-[400px] -top-10 left-3/4 transform translate-x-[10%] text-justify">
                            This chart shows the total number of clicks on your advertisement across all the venues.
                        </div>
                    </span>
                </h3>
                {error ? (
                    <ErrorNotice message={error} />
                ) : (
                    <Line data={totalRecordsLineData} options={optionsForLineChart} />
                )}
            </div>

            <div className="flex gap-4 items-center mt-10 mb-10">
                <label className="text-lg font-medium">
                    Select Spot:
                    <select
                        value={selectedSpotId}
                        onChange={handleSpotChange}
                        className="mt-2 p-2 border rounded"
                    >
                        <option value="all">All Spots</option>
                        {uniqueSpots.map(([spotId, spotName]) => (
                            <option key={spotId} value={spotId}>
                                {spotName}
                            </option>
                        ))}
                    </select>
                </label>
            </div>

            <div className="h-[300px] mb-4">
                <h3 className="text-center font-semibold">
                    Daily Venue Clicks
                    <span className="ml-2 relative group cursor-pointer">
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-300 text-gray-700 text-sm font-bold">
                            ?
                        </span>
                        <div className="absolute hidden group-hover:block bg-white text-gray-700 text-sm p-2 rounded shadow-md whitespace-normal w-[400px] -top-10 left-3/4 transform translate-x-[10%] text-justify">
                            This chart shows the daily click trends of advertisements for each venue, helping you analyze the performance across different venues.
                            And you can use the buttons on the left to switch between different Venues
                        </div>
                    </span>
                </h3>
                {loading ? (
                    <p className="text-lg font-semibold text-gray-500">Loading...</p>
                ) : error ? (
                    <ErrorNotice message={error} />
                ) : (
                    <Line data={filteredSpotTotalsData} options={optionsForMutiLine} />
                )}
            </div>
        </div>
    );
};

export default AdvLogDashboard;
